import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [activeRole, setActiveRole] = useState(localStorage.getItem('activeRole') || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('access_token');

      if (storedUser && token) {
        let parsedUser = null;
        try {
          parsedUser = JSON.parse(storedUser);
        } catch (parseError) {
          console.error('Failed to parse stored user, clearing auth state:', parseError);
          localStorage.removeItem('user');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('activeRole');
        }

        if (parsedUser) {
          setUser(parsedUser);

          // Initialize activeRole if not set
          if (!activeRole) {
            const initialRole = parsedUser.roles && parsedUser.roles.length > 0
              ? (parsedUser.roles.includes('EMPLOYEE') ? 'EMPLOYEE' : (parsedUser.roles.includes('COMPANY_ADMIN') ? 'COMPANY_ADMIN' : parsedUser.roles[0]))
              : (parsedUser.role || 'EMPLOYEE');
            setActiveRole(initialRole);
            localStorage.setItem('activeRole', initialRole);
          }
        }
      }
    } catch (e) {
      console.error('AuthProvider initialization error:', e);
    } finally {
      setLoading(false);
    }
  }, [activeRole]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, refresh_token, user: userData } = response.data;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));

    const initialRole = userData.roles && userData.roles.length > 0 
      ? (userData.roles.includes('EMPLOYEE') ? 'EMPLOYEE' : (userData.roles.includes('COMPANY_ADMIN') ? 'COMPANY_ADMIN' : userData.roles[0]))
      : (userData.role || 'EMPLOYEE');
    
    setActiveRole(initialRole);
    localStorage.setItem('activeRole', initialRole);

    setUser(userData);
    return userData;
  };

  const switchRole = (role) => {
    setActiveRole(role);
    localStorage.setItem('activeRole', role);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('activeRole');
      setUser(null);
      setActiveRole(null);
    }
  };

  const normalizeRole = (role) =>
    (role || '').toUpperCase().replace(/\s+/g, '_').replace(/-+/g, '_');

  const isSuperAdmin = () =>
    normalizeRole(user?.role) === 'SUPER_ADMIN' || user?.role_id === 'SUPER_ADMIN';

  const isCompanyAdmin = () => {
    const normalized = normalizeRole(activeRole || user?.role);
    return normalized === 'COMPANY_ADMIN';
  };

  const isHRManager = () => {
    const normalized = normalizeRole(activeRole || user?.role);
    return normalized === 'HR_MANAGER';
  };

  const isEmployee = () => {
    const normalized = normalizeRole(activeRole);
    if (normalized) return normalized === 'EMPLOYEE';
    if (!user || isSuperAdmin()) return false;
    const role = normalizeRole(user.role);
    return role === 'EMPLOYEE' || !!user.employee_id;
  };

  const isAdminOrHR = () => {
    const normalized = normalizeRole(activeRole || user?.role);
    if (normalized === 'COMPANY_ADMIN' || normalized === 'HR_MANAGER') return true;
    if (!user || isSuperAdmin()) return false;
    return !!user.role_id && normalized !== 'EMPLOYEE';
  };

  const value = {
    user,
    activeRole,
    loading,
    login,
    logout,
    switchRole,
    isSuperAdmin,
    isCompanyAdmin,
    isHRManager,
    isEmployee,
    isAdminOrHR,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
